import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, Loader2, AlertCircle, Calendar, Clock, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling'); // polling, paid, error
  const [paymentData, setPaymentData] = useState(null);

  const pollPaymentStatus = useCallback(async (attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2500;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout-status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('paid');
        setPaymentData(data);
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Errore verifica pagamento:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-white py-20" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-4 text-center">
        {status === 'polling' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-blue-50 rounded-2xl p-12"
            data-testid="payment-polling"
          >
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Verifica del pagamento in corso...
            </h2>
            <p className="text-slate-600">
              Stiamo verificando il tuo pagamento. Attendi qualche istante.
            </p>
          </motion.div>
        )}

        {status === 'paid' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 rounded-2xl p-12"
            data-testid="payment-confirmed"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Pagamento confermato!
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              La tua consulenza è stata prenotata e il pagamento è stato ricevuto con successo.
              Riceverai una email di conferma con tutti i dettagli.
            </p>

            {paymentData && (
              <div className="bg-white rounded-xl p-6 mb-8 text-left border border-green-200">
                <h3 className="font-semibold text-slate-900 mb-3">Riepilogo</h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex justify-between">
                    <span>Importo pagato:</span>
                    <span className="font-semibold text-slate-900">
                      {paymentData.amount_total ? `€${(paymentData.amount_total / 100).toFixed(2)}` : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Stato:</span>
                    <span className="font-semibold text-green-600">Confermato</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/area-clienti">
                <Button className="btn-accent" data-testid="go-to-dashboard">
                  <Calendar className="w-4 h-4 mr-2" />
                  Area Clienti
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" data-testid="go-to-home">
                  Torna alla Home
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {(status === 'error' || status === 'timeout') && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-amber-50 rounded-2xl p-12"
            data-testid="payment-error"
          >
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              {status === 'timeout' ? 'Verifica in attesa' : 'Errore nella verifica'}
            </h2>
            <p className="text-slate-600 mb-6">
              {status === 'timeout'
                ? 'La verifica del pagamento sta richiedendo più tempo del previsto. Il pagamento potrebbe essere stato comunque elaborato. Controlla la tua email per la conferma.'
                : 'Si è verificato un errore durante la verifica del pagamento. Se hai completato il pagamento, controlla la tua email.'
              }
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/consulenza">
                <Button className="btn-accent" data-testid="retry-booking">
                  Nuova Prenotazione
                </Button>
              </Link>
              <Link to="/contatti">
                <Button variant="outline" data-testid="contact-support">
                  Contattaci
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
